import { createContext, useState, useEffect } from "react";
import API from "../api/api"; // Added API import for backend logout

export const AuthContext = createContext();

// =========================
// SESSION-SCOPED CACHE KEYS
// =========================
// These keys hold data that belongs to ONE specific account (address,
// cart, live orders, coupons). They must never leak from one account
// to the next on the same browser/device — so they get wiped on every
// login (covers fresh registration + switching accounts) and on logout.
const SESSION_CACHE_KEYS = [
  "selectedAddress",
  "addressUpdated",
  "mealnoCart",
  "activeOrders",
  "cachedLiveOrders",
  "selectedCoupon",
  "couponDiscount",
];

const clearSessionCache = () => {
  SESSION_CACHE_KEYS.forEach((key) => localStorage.removeItem(key));
  // Let any already-mounted listeners (BottomScrollableBar, LandingHome, etc.)
  // know the cache changed so they re-read fresh state immediately.
  window.dispatchEvent(new Event("storage"));
};

export default function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      const stored = localStorage.getItem("user");
      return stored && stored !== "undefined" ? JSON.parse(stored) : null;
    } catch (err) {
      return null;
    }
  });

  // =========================
  // SYNC ACTIVE ORDERS ON LOGIN
  // =========================
  useEffect(() => {
    const syncActiveOrders = async () => {
      // Only run this if a user is actively logged in
      if (user && user.id) {
        try {
          // ✅ Updated to use your exact backend route
          const res = await API.get(`/user/${user.id}/orders?skip=0&limit=100`);

          if (res.data && Array.isArray(res.data)) {
            // Filter out orders that are already finished or cancelled
            const activeOrders = res.data.filter(order =>
              !["DELIVERED", "REJECTED", "CANCELLED"].includes(order.status)
            );

            // If active orders exist, save their IDs back to local storage
            if (activeOrders.length > 0) {
              const activeIds = activeOrders.map(o => o.order_id);
              localStorage.setItem("activeOrders", JSON.stringify(activeIds));

              // Dispatch a storage event to instantly wake up the BottomScrollableBar
              window.dispatchEvent(new Event("storage"));
            }
          }
        } catch (err) {
          console.error("Could not sync active orders on login:", err);
        }
      }
    };

    syncActiveOrders();
  }, [user]);

  const login = (data) => {
    // 🧹 Wipe any leftover address/cart/order data from a previous
    // account on this device BEFORE loading the new one in, so a
    // fresh registration or a different account never inherits it.
    clearSessionCache();

    // ✅ Store the new dual tokens securely
    localStorage.setItem("user_access_token", data.access_token);
    localStorage.setItem("user_refresh_token", data.refresh_token);

    localStorage.setItem("user", JSON.stringify(data.user));
    setUser(data.user);
  };

  const logout = async () => {
    // ✅ Notify backend to blocklist token in Redis/DB before clearing client
    try {
      const currentToken = localStorage.getItem("user_access_token");
      if (currentToken) {
        await API.post("/auth/logout");
      }
    } catch (error) {
    }

    localStorage.removeItem("user");
    localStorage.removeItem("user_access_token");
    localStorage.removeItem("user_refresh_token");
    clearSessionCache(); // 🧹 don't leave address/cart/orders behind for the next login
    setUser(null);
    window.location.href = "/login"; // Force redirect to login page
  };

  return (
    <AuthContext.Provider value={{ user, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}